import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchServiceProfile, deleteServiceProfile, testServiceAuth, refreshServiceContext, addAuthStrategy, deleteAuthStrategy, extractDesignTokens } from '@/services/serviceProfiles';
import { createDesignSystemRedesign } from '@/services/redesigns';
import { ServiceForm } from '@/components/ServiceForm';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const labelStyle: React.CSSProperties = {
  fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, minWidth: '120px',
};

const valueStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--text-primary)' };

const sectionStyle: React.CSSProperties = {
  marginBottom: '24px', padding: '16px', border: '1px solid var(--color-border)', borderRadius: '8px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '14px', fontWeight: 700, marginBottom: '12px', marginTop: 0,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
  fontSize: '11px', fontWeight: 600, marginRight: '4px',
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', border: 'none',
};

function relativeTime(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddStrategy, setShowAddStrategy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [expandedAspects, setExpandedAspects] = useState<Set<string> | null>(null);
  const [refreshMessage, setRefreshMessage] = useState<{ text: string; status: string } | null>(null);
  const [dsStatus, setDsStatus] = useState<{ text: string; type: 'loading' | 'success' | 'error' } | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['serviceProfile', id],
    queryFn: () => fetchServiceProfile(id!),
    enabled: !!id,
    refetchInterval: 10_000,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteServiceProfile(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceProfiles'] });
      navigate('/ui-orchestrator/services');
    },
  });

  const testAuthMutation = useMutation({
    mutationFn: () => testServiceAuth(id!),
    onSuccess: (result) => setTestResult(result),
    onError: (err: Error) => setTestResult({ success: false, message: err.message }),
  });

  const refreshContextMutation = useMutation({
    mutationFn: () => refreshServiceContext(id!, true),
    onSuccess: (result) => {
      setRefreshMessage({ text: result.message, status: result.status });
      queryClient.invalidateQueries({ queryKey: ['serviceProfile', id] });
    },
    onError: (err: Error) => {
      setRefreshMessage({ text: err.message, status: 'error' });
    },
  });

  const addStrategyMutation = useMutation({
    mutationFn: (data: Parameters<typeof addAuthStrategy>[1]) => addAuthStrategy(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceProfile', id] });
      setShowAddStrategy(false);
    },
  });

  const deleteStrategyMutation = useMutation({
    mutationFn: (name: string) => deleteAuthStrategy(id!, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceProfile', id] });
    },
  });

  if (isLoading) return <p style={{ color: 'var(--text-muted)' }}>Loading...</p>;
  if (!profile) return <p style={{ color: 'var(--color-error)' }}>Service profile not found.</p>;

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value || '-'}</span>
    </div>
  );

  return (
    <div>
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Service"
          message={`This will permanently delete "${profile.displayName}" and all its configuration. This action cannot be undone.`}
          confirmLabel="Delete"
          isLoading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
      {strategyToDelete && (
        <ConfirmDialog
          title="Delete Auth Strategy"
          message={`Delete the "${strategyToDelete}" authentication strategy? This cannot be undone.`}
          confirmLabel="Delete"
          isLoading={deleteStrategyMutation.isPending}
          onConfirm={() => {
            deleteStrategyMutation.mutate(strategyToDelete, {
              onSuccess: () => setStrategyToDelete(null),
            });
          }}
          onCancel={() => setStrategyToDelete(null)}
        />
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <button onClick={() => navigate('/ui-orchestrator/services')}
            style={{ ...buttonStyle, backgroundColor: 'transparent', color: 'var(--color-primary)', padding: '0', marginBottom: '8px', fontSize: '12px' }}>
            &larr; Back to Services
          </button>
          <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>{profile.displayName}</h1>
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>{profile.repo}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowEditForm(true)}
            style={{ ...buttonStyle, backgroundColor: '#2563eb', color: '#fff' }}>Edit</button>
          <button onClick={() => setShowDeleteConfirm(true)}
            style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626' }}>Delete</button>
        </div>
      </div>

      {showEditForm && <ServiceForm onClose={() => setShowEditForm(false)} existingProfile={profile} />}

      {/* Basic Info */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Basic Info</h3>
        <Row label="Description" value={profile.description} />
        <Row label="Preview URL" value={profile.previewUrl} />
        <Row label="Base Branch" value={profile.baseBranch} />
      </div>

      {/* Authentication */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Authentication</h3>
        <Row label="Auth Type" value={
          <span style={{ ...chipStyle, backgroundColor: profile.auth?.type === 'none' ? 'var(--bg-secondary)' : '#dbeafe', color: profile.auth?.type === 'none' ? 'var(--text-muted)' : '#1d4ed8' }}>
            {profile.auth?.type || 'none'}
          </span>
        } />
        {profile.auth?.type !== 'none' && (
          <>
            <Row label="Login Path" value={profile.auth?.loginPath} />
            {profile.auth?.credentials_masked && Object.keys(profile.auth.credentials_masked).length > 0 && (
              <Row label="Credentials" value={
                <div>
                  {Object.entries(profile.auth.credentials_masked).map(([key, masked]) => (
                    <div key={key} style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                      {key}: {masked as string}
                    </div>
                  ))}
                </div>
              } />
            )}
            {profile.auth?.extraFields && Object.keys(profile.auth.extraFields).length > 0 && (
              <Row label="Extra Fields" value={
                <div>
                  {Object.entries(profile.auth.extraFields).map(([key, val]) => (
                    <div key={key} style={{ fontSize: '12px', fontFamily: 'monospace' }}>{key}: {val as string}</div>
                  ))}
                </div>
              } />
            )}
          </>
        )}
        <div style={{ marginTop: '12px' }}>
          <button onClick={() => testAuthMutation.mutate()} disabled={testAuthMutation.isPending}
            style={{ ...buttonStyle, backgroundColor: '#f0fdf4', color: '#166534', fontSize: '12px', padding: '6px 12px' }}>
            {testAuthMutation.isPending ? 'Testing...' : 'Test Auth'}
          </button>
          {testResult && (
            <span style={{
              marginLeft: '12px', fontSize: '12px', fontWeight: 600,
              color: testResult.success ? '#166534' : '#dc2626',
            }}>
              {testResult.success ? 'Success' : 'Failed'}: {testResult.message}
            </span>
          )}
        </div>
      </div>

      {/* Auth Strategies */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>Auth Strategies</h3>
          <button onClick={() => setShowAddStrategy(!showAddStrategy)}
            style={{ ...buttonStyle, backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '12px', padding: '6px 12px' }}>
            {showAddStrategy ? 'Cancel' : 'Add Strategy'}
          </button>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          Named login configurations for different user roles. The default strategy is used for screenshots when none is specified.
        </div>
        {showAddStrategy && (
          <AddAuthStrategyForm
            onSubmit={(data) => addStrategyMutation.mutate(data)}
            isPending={addStrategyMutation.isPending}
            error={addStrategyMutation.error?.message}
          />
        )}
        {(profile.authStrategies ?? []).length === 0 ? (
          <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            No auth strategies configured. Using legacy auth settings above.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {profile.authStrategies.map(strategy => (
              <div key={strategy.name} style={{ padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: strategy.isDefault ? '#f0fdf4' : 'var(--color-card-surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>{strategy.name}</span>
                    <span style={{ ...chipStyle,
                      backgroundColor: strategy.type === 'cookie' ? '#dbeafe' : strategy.type === 'bearer' ? '#fef3c7' : strategy.type === 'firebase_token' ? '#fce7f3' : 'var(--bg-secondary)',
                      color: strategy.type === 'cookie' ? '#1d4ed8' : strategy.type === 'bearer' ? '#92400e' : strategy.type === 'firebase_token' ? '#be185d' : 'var(--text-muted)',
                    }}>
                      {strategy.type === 'firebase_token' ? 'firebase' : strategy.type}
                    </span>
                    {strategy.isDefault && (
                      <span style={{ ...chipStyle, backgroundColor: '#dcfce7', color: '#166534' }}>default</span>
                    )}
                  </div>
                  <button onClick={() => setStrategyToDelete(strategy.name)}
                    style={{ ...buttonStyle, backgroundColor: '#fee2e2', color: '#dc2626', padding: '4px 8px', fontSize: '11px' }}>Delete</button>
                </div>
                {strategy.description && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>{strategy.description}</div>}
                <div style={{ fontSize: '12px', marginTop: '6px', display: 'flex', gap: '16px', color: 'var(--text-secondary)' }}>
                  <span>Path: <code style={{ fontSize: '11px' }}>{strategy.loginPath}</code></span>
                  {strategy.credentials_masked && Object.keys(strategy.credentials_masked).length > 0 && (
                    <span>Creds: {Object.keys(strategy.credentials_masked).join(', ')}</span>
                  )}
                  {strategy.extraFields && Object.keys(strategy.extraFields).length > 0 && (
                    <span>Extra: {Object.entries(strategy.extraFields).map(([k, v]) => `${k}=${v}`).join(', ')}</span>
                  )}
                  {strategy.bodyFieldMapping && Object.keys(strategy.bodyFieldMapping).length > 0 && (
                    <span>Mapping: {Object.entries(strategy.bodyFieldMapping).map(([k, v]) => `${k}\u2192${v}`).join(', ')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tech Stack */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Tech Stack</h3>
        <Row label="Framework" value={profile.techStack?.framework} />
        <Row label="UI Library" value={profile.techStack?.uiLibrary} />
        <Row label="Design System" value={profile.techStack?.designSystem} />
        {profile.techStack?.designSystemFigmaKey && (
          <Row label="Figma Key" value={profile.techStack.designSystemFigmaKey} />
        )}
      </div>

      {/* User Personas */}
      {profile.userPersonas && profile.userPersonas.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>User Personas</h3>
          {profile.userPersonas.map((persona, i) => (
            <div key={i} style={{ marginBottom: '10px', padding: '8px', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{persona.name}</div>
              {persona.description && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{persona.description}</div>}
              {persona.primaryFlows && persona.primaryFlows.length > 0 && (
                <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {persona.primaryFlows.map((flow) => (
                    <span key={flow} style={{ ...chipStyle, backgroundColor: '#ede9fe', color: '#5b21b6' }}>{flow}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Design Guidelines */}
      {profile.designGuidelines && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Design Guidelines</h3>
          <pre style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
            {profile.designGuidelines}
          </pre>
        </div>
      )}

      {/* Design System */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>Design System</h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            {profile.designSystemMd && (
              <button
                onClick={async () => {
                  setDsStatus({ text: 'Creating design system project...', type: 'loading' });
                  try {
                    const result = await createDesignSystemRedesign(id!);
                    if (result.redesignId) {
                      navigate(`/ui-orchestrator/projects/redesign-${result.redesignId}`);
                    } else {
                      setDsStatus({ text: result.message || 'Project created', type: 'success' });
                      setTimeout(() => setDsStatus(null), 3000);
                    }
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    setDsStatus({ text: msg, type: 'error' });
                  }
                }}
                disabled={dsStatus?.type === 'loading'}
                style={{ ...buttonStyle, backgroundColor: dsStatus?.type === 'loading' ? 'var(--bg-disabled)' : '#dbeafe', color: '#1d4ed8', fontSize: '12px', padding: '6px 12px' }}
              >
                {dsStatus?.type === 'loading' ? 'Creating...' : 'Manage in Figma'}
              </button>
            )}
            <button
              onClick={async () => {
                setDsStatus({ text: 'Extracting tokens from repo...', type: 'loading' });
                try {
                  const result = await extractDesignTokens(id!);
                  setDsStatus({ text: result.message, type: 'success' });
                  queryClient.invalidateQueries({ queryKey: ['serviceProfile', id] });
                  setTimeout(() => setDsStatus(null), 5000);
                } catch (err) {
                  const msg = err instanceof Error ? err.message : String(err);
                  setDsStatus({ text: msg, type: 'error' });
                }
              }}
              disabled={dsStatus?.type === 'loading'}
              style={{ ...buttonStyle, backgroundColor: dsStatus?.type === 'loading' ? 'var(--bg-disabled)' : '#dcfce7', color: '#166534', fontSize: '12px', padding: '6px 12px' }}
            >
              {dsStatus?.type === 'loading' ? 'Extracting...' : 'Extract from Repo'}
            </button>
            <button
              onClick={() => navigate(`/ui-orchestrator/services/${id}/design-system`)}
              style={{ ...buttonStyle, backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '12px', padding: '6px 12px' }}
            >
              {profile.designSystemMd ? 'Edit Design System' : 'Add Design System'}
            </button>
          </div>
        </div>
        {dsStatus && (
          <div style={{
            marginTop: '8px', padding: '8px 12px', borderRadius: '6px', fontSize: '12px',
            backgroundColor: dsStatus.type === 'error' ? '#fef2f2' : dsStatus.type === 'success' ? '#f0fdf4' : '#f0f9ff',
            color: dsStatus.type === 'error' ? '#dc2626' : dsStatus.type === 'success' ? '#166534' : '#1d4ed8',
          }}>
            {dsStatus.text}
          </div>
        )}
        {profile.designSystemMd ? (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {profile.designSystemMd.split('\n').length} lines configured
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            No design system configured yet.
          </div>
        )}
      </div>

      {/* Unblocked Context */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>Unblocked Context</h3>
          <button onClick={() => refreshContextMutation.mutate()} disabled={refreshContextMutation.isPending}
            style={{ ...buttonStyle, backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '12px', padding: '6px 12px' }}>
            {refreshContextMutation.isPending ? 'Refreshing...' : 'Refresh Context'}
          </button>
        </div>
        {refreshMessage && (
          <div style={{
            fontSize: '12px', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px',
            backgroundColor: refreshMessage.status === 'error' ? '#fef2f2' : '#f0fdf4',
            color: refreshMessage.status === 'error' ? '#dc2626' : '#166534',
          }}>
            {refreshMessage.text}
          </div>
        )}
        {profile.unblockedContext?.lastFetched ? (
          <>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
              Last updated: {relativeTime(profile.unblockedContext.lastFetched)}
              {' · '}{new Date(profile.unblockedContext.lastFetched).toLocaleString()}
            </div>
            {profile.unblockedContext.cachedAnswers.length === 0 ? (
              <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No answers cached yet.</p>
            ) : (
              profile.unblockedContext.cachedAnswers.map((cached, idx) => {
                // Auto-expand first answer when user hasn't interacted yet
                const isExpanded = expandedAspects === null ? idx === 0 : expandedAspects.has(cached.aspect);
                const toggle = () => {
                  setExpandedAspects(prev => {
                    // On first interaction, initialize from the auto-expanded state
                    const current = prev === null
                      ? new Set([profile!.unblockedContext!.cachedAnswers[0]?.aspect])
                      : new Set(prev);
                    if (current.has(cached.aspect)) current.delete(cached.aspect);
                    else current.add(cached.aspect);
                    return current;
                  });
                };
                const aspectLabels: Record<string, string> = {
                  techStack: 'Tech Stack',
                  components: 'UI Components',
                  authentication: 'Authentication',
                };
                return (
                  <div key={cached.aspect} style={{ marginBottom: '8px', border: '1px solid var(--color-border)', borderRadius: '6px', overflow: 'hidden' }}>
                    <button onClick={toggle} type="button"
                      style={{
                        width: '100%', textAlign: 'left', padding: '10px 12px',
                        backgroundColor: isExpanded ? 'var(--bg-secondary)' : 'var(--color-card-surface)',
                        border: 'none', cursor: 'pointer', display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center',
                        fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)',
                      }}>
                      <span>{aspectLabels[cached.aspect] || cached.aspect}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {isExpanded ? '▼' : '▶'} {cached.references?.length || 0} refs
                      </span>
                    </button>
                    {isExpanded && (
                      <div style={{ padding: '12px', borderTop: '1px solid var(--color-border)' }}>
                        <pre style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', margin: '0 0 8px 0', fontFamily: 'inherit', lineHeight: 1.5 }}>
                          {cached.answer}
                        </pre>
                        {cached.references && cached.references.length > 0 && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--bg-secondary)' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>References:</div>
                            {cached.references.map((ref, ri) => (
                              <div key={ri} style={{ fontSize: '11px', marginBottom: '2px' }}>
                                <span style={{ ...chipStyle, backgroundColor: ref.sourceType === 'github' ? '#f0fdf4' : 'var(--bg-secondary)', color: ref.sourceType === 'github' ? '#166534' : 'var(--text-muted)' }}>
                                  {ref.sourceType}
                                </span>
                                <a href={ref.htmlUrl} target="_blank" rel="noopener noreferrer"
                                  style={{ color: 'var(--color-primary)', textDecoration: 'none', marginLeft: '4px' }}>
                                  {ref.title || ref.htmlUrl}
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        ) : (
          <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            No context enrichment yet. Click "Refresh Context" to query Unblocked for service information.
          </p>
        )}
      </div>
    </div>
  );
}

const formInputStyle: React.CSSProperties = {
  padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: '6px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};

function AddAuthStrategyForm({ onSubmit, isPending, error }: {
  onSubmit: (data: {
    name: string;
    description?: string;
    type: 'cookie' | 'bearer' | 'firebase_token' | 'none';
    loginPath?: string;
    credentials?: Record<string, string>;
    extraFields?: Record<string, string>;
    bodyFieldMapping?: Record<string, string>;
    isDefault?: boolean;
  }) => void;
  isPending: boolean;
  error?: string;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'cookie' | 'bearer' | 'firebase_token' | 'none'>('cookie');
  const [loginPath, setLoginPath] = useState('/api/auth/login');
  const [credKeys, setCredKeys] = useState(['email', 'password']);
  const [credVals, setCredVals] = useState<Record<string, string>>({});
  const [extraKeys, setExtraKeys] = useState<string[]>([]);
  const [extraVals, setExtraVals] = useState<Record<string, string>>({});
  const [mappingKeys, setMappingKeys] = useState<string[]>([]);
  const [mappingVals, setMappingVals] = useState<Record<string, string>>({});
  const [isDefault, setIsDefault] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const credentials: Record<string, string> = {};
    for (const key of credKeys) { if (key && credVals[key]) credentials[key] = credVals[key]; }
    const extraFields: Record<string, string> = {};
    for (const key of extraKeys) { if (key && extraVals[key]) extraFields[key] = extraVals[key]; }
    const bodyFieldMapping: Record<string, string> = {};
    for (const key of mappingKeys) { if (key && mappingVals[key]) bodyFieldMapping[key] = mappingVals[key]; }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      type,
      loginPath: loginPath.trim(),
      ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
      ...(Object.keys(extraFields).length > 0 ? { extraFields } : {}),
      ...(Object.keys(bodyFieldMapping).length > 0 ? { bodyFieldMapping } : {}),
      isDefault,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '6px', marginBottom: '12px', backgroundColor: 'var(--bg-secondary)' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Name *</label>
          <input style={formInputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. super-admin" required />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Auth Type</label>
          <select style={formInputStyle} value={type} onChange={e => {
            const newType = e.target.value as 'cookie' | 'bearer' | 'firebase_token' | 'none';
            setType(newType);
            if (newType === 'firebase_token') {
              if (!extraKeys.includes('firebaseApiKey')) {
                setExtraKeys(prev => [...prev, 'firebaseApiKey']);
              }
              if (!mappingKeys.includes('idToken')) {
                setMappingKeys(prev => [...prev, 'idToken']);
              }
            }
          }}>
            <option value="cookie">Cookie</option>
            <option value="bearer">Bearer</option>
            <option value="firebase_token">Firebase Token</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>
      {type === 'firebase_token' && (
        <div style={{ fontSize: '11px', color: '#92400e', backgroundColor: '#fef3c7', padding: '8px 10px', borderRadius: '6px', marginBottom: '8px' }}>
          Firebase Token auth exchanges email/password for a Firebase ID token, then sends it to the app's login endpoint.
          Requires: <strong>email</strong> + <strong>password</strong> in credentials, and <strong>firebaseApiKey</strong> in extra fields (your Firebase Web API key from the Firebase console).
        </div>
      )}
      <div style={{ marginBottom: '8px' }}>
        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Description</label>
        <input style={formInputStyle} value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Super admin with full access" />
      </div>
      {type !== 'none' && (
        <>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Login Path</label>
            <input style={formInputStyle} value={loginPath} onChange={e => setLoginPath(e.target.value)} placeholder="/api/auth/login" />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Credentials</label>
            {credKeys.map((key, i) => (
              <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                <input style={{ ...formInputStyle, flex: 1 }} value={key} placeholder="key"
                  onChange={e => { const nk = [...credKeys]; nk[i] = e.target.value; setCredKeys(nk); }} />
                <input style={{ ...formInputStyle, flex: 2 }} type="password" placeholder="value"
                  value={credVals[key] || ''} onChange={e => setCredVals({ ...credVals, [key]: e.target.value })} />
                <button type="button" onClick={() => setCredKeys(credKeys.filter((_, j) => j !== i))}
                  style={{ padding: '4px 6px', border: 'none', background: '#fee2e2', color: '#dc2626', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>x</button>
              </div>
            ))}
            <button type="button" onClick={() => setCredKeys([...credKeys, ''])}
              style={{ marginTop: '4px', padding: '2px 8px', border: 'none', background: 'var(--bg-secondary)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>+ Add</button>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Extra Fields (non-secret)</label>
            {extraKeys.map((key, i) => (
              <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                <input style={{ ...formInputStyle, flex: 1 }} value={key} placeholder="key"
                  onChange={e => { const nk = [...extraKeys]; nk[i] = e.target.value; setExtraKeys(nk); }} />
                <input style={{ ...formInputStyle, flex: 2 }} placeholder="value"
                  value={extraVals[key] || ''} onChange={e => setExtraVals({ ...extraVals, [key]: e.target.value })} />
                <button type="button" onClick={() => setExtraKeys(extraKeys.filter((_, j) => j !== i))}
                  style={{ padding: '4px 6px', border: 'none', background: '#fee2e2', color: '#dc2626', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>x</button>
              </div>
            ))}
            <button type="button" onClick={() => setExtraKeys([...extraKeys, ''])}
              style={{ marginTop: '4px', padding: '2px 8px', border: 'none', background: 'var(--bg-secondary)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>+ Add</button>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Body Field Mapping</label>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
              Renames fields in the login request body. Source &rarr; target.
              {type === 'firebase_token' && ' "idToken" is the Firebase token field.'}
            </div>
            {mappingKeys.map((key, i) => (
              <div key={i} style={{ display: 'flex', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                <input style={{ ...formInputStyle, flex: 1 }} value={key} placeholder="source field"
                  onChange={e => { const nk = [...mappingKeys]; nk[i] = e.target.value; setMappingKeys(nk); }} />
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>&rarr;</span>
                <input style={{ ...formInputStyle, flex: 1 }} placeholder="target field"
                  value={mappingVals[key] || ''} onChange={e => setMappingVals({ ...mappingVals, [key]: e.target.value })} />
                <button type="button" onClick={() => setMappingKeys(mappingKeys.filter((_, j) => j !== i))}
                  style={{ padding: '4px 6px', border: 'none', background: '#fee2e2', color: '#dc2626', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>x</button>
              </div>
            ))}
            <button type="button" onClick={() => setMappingKeys([...mappingKeys, ''])}
              style={{ marginTop: '4px', padding: '2px 8px', border: 'none', background: 'var(--bg-secondary)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>+ Add Mapping</button>
          </div>
        </>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
          Set as default strategy
        </label>
      </div>
      {error && <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '8px' }}>{error}</div>}
      <button type="submit" disabled={isPending || !name.trim()}
        style={{ padding: '6px 14px', border: 'none', borderRadius: '6px', background: isPending ? '#93c5fd' : '#2563eb', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
        {isPending ? 'Adding...' : 'Add Strategy'}
      </button>
    </form>
  );
}
